/**
 * Personal — tipos y etiquetas de las fichas de empleado.
 *
 * Gemelo de `server/src/services/personal/esquema.ts`. Al añadir un tipo de
 * contrato, un motivo de baja o un tipo de ausencia allí, hay que añadirlos
 * aquí o la pantalla enseñará el código crudo. No hay paquete compartido entre
 * cliente y servidor; es el mismo trato que tienen `lib/candidatos.ts` y
 * `lib/peticiones.ts`.
 *
 * Los PUESTOS y las ZONAS no están aquí a propósito: son ajustes editables
 * desde Configuración y se leen con `api.getSettings()`.
 *
 * Ojo con los campos sensibles: el DNI, el número de la Seguridad Social, el
 * IBAN y los salarios NO vienen en `Empleado`. Se piden aparte con
 * `api.getDatosSensibles()`, y esa lectura queda registrada en la actividad.
 */

export type Situacion = 'alta' | 'baja';

export type TipoContrato =
  | 'indefinido'
  | 'temporal'
  | 'fijo_discontinuo'
  | 'formacion'
  | 'practicas'
  | 'autonomo';

export type Jornada = 'completa' | 'parcial';

export type MotivoBaja =
  | 'fin_contrato'
  | 'voluntaria'
  | 'no_supera_prueba'
  | 'despido'
  | 'mutuo_acuerdo'
  | 'otro';

export type TipoDocumentoIdentidad = 'dni' | 'nie' | 'pasaporte';

export type TipoDocumento =
  | 'contrato'
  | 'dni'
  | 'alta_ss'
  | 'nomina'
  | 'certificado'
  | 'carnet_conducir'
  | 'formacion_prl'
  | 'otro';

export type TipoAusencia =
  | 'vacaciones'
  | 'baja_medica'
  | 'permiso'
  | 'asuntos_propios'
  | 'injustificada'
  | 'otro';

export type NivelIdioma = 'basico' | 'medio' | 'alto' | 'nativo';
export type Ternario = 'si' | 'no' | 'desconocido';

// ─── Etiquetas ────────────────────────────────────────────────────────────

export const SITUACION_LABEL: Record<Situacion, string> = {
  alta: 'De alta',
  baja: 'De baja',
};

export const TIPO_CONTRATO_LABEL: Record<TipoContrato, string> = {
  indefinido: 'Indefinido',
  temporal: 'Temporal',
  fijo_discontinuo: 'Fijo discontinuo',
  formacion: 'Formación',
  practicas: 'Prácticas',
  autonomo: 'Autónomo',
};

/** Los que exigen fecha de fin. Gemelo de `CONTRATOS_CON_FIN` del servidor. */
export const CONTRATOS_CON_FIN: TipoContrato[] = ['temporal', 'formacion', 'practicas'];

export const JORNADA_LABEL: Record<Jornada, string> = {
  completa: 'Completa',
  parcial: 'Parcial',
};

export const MOTIVO_BAJA_LABEL: Record<MotivoBaja, string> = {
  fin_contrato: 'Fin de contrato',
  voluntaria: 'Baja voluntaria',
  no_supera_prueba: 'No supera el periodo de prueba',
  despido: 'Despido',
  mutuo_acuerdo: 'Mutuo acuerdo',
  otro: 'Otro',
};

export const TIPO_DOCUMENTO_IDENTIDAD_LABEL: Record<TipoDocumentoIdentidad, string> = {
  dni: 'DNI',
  nie: 'NIE',
  pasaporte: 'Pasaporte',
};

export const TIPO_DOCUMENTO_LABEL: Record<TipoDocumento, string> = {
  contrato: 'Contrato',
  dni: 'Documento de identidad',
  alta_ss: 'Alta en la Seguridad Social',
  nomina: 'Nómina',
  certificado: 'Certificado',
  carnet_conducir: 'Carnet de conducir',
  formacion_prl: 'Formación PRL',
  otro: 'Otro',
};

export const TIPO_AUSENCIA_LABEL: Record<TipoAusencia, string> = {
  vacaciones: 'Vacaciones',
  baja_medica: 'Baja médica',
  permiso: 'Permiso',
  asuntos_propios: 'Asuntos propios',
  injustificada: 'Injustificada',
  otro: 'Otro',
};

/**
 * Color de cada ausencia en la cuadrícula del equipo.
 *
 * Se eligen distinguibles entre sí y con el gris de los días fuera de alta:
 * la cuadrícula se lee de un vistazo o no sirve para nada.
 */
export const TIPO_AUSENCIA_COLOR: Record<TipoAusencia, string> = {
  vacaciones: 'bg-sky-400',
  baja_medica: 'bg-rose-400',
  permiso: 'bg-violet-400',
  asuntos_propios: 'bg-amber-400',
  injustificada: 'bg-red-600',
  otro: 'bg-slate-400',
};

export const NIVEL_IDIOMA_LABEL: Record<NivelIdioma, string> = {
  basico: 'Básico',
  medio: 'Medio',
  alto: 'Alto',
  nativo: 'Nativo',
};

export const TERNARIO_LABEL: Record<Ternario, string> = {
  si: 'Sí',
  no: 'No',
  desconocido: 'No consta',
};

/** Etiquetas que pone la importación desde la app de lavandería. */
export const ETIQUETA_CUENTA = 'cuenta_compartida';
export const ETIQUETA_FECHA_APROXIMADA = 'fecha_alta_aproximada';
export const ETIQUETA_ARCHIVADO = 'archivado_en_lavanderia';

export const ETIQUETA_LABEL: Record<string, string> = {
  [ETIQUETA_CUENTA]: 'Cuenta compartida',
  [ETIQUETA_FECHA_APROXIMADA]: 'Fecha de alta aproximada',
  [ETIQUETA_ARCHIVADO]: 'Archivado en lavandería',
};

// ─── Estructuras ──────────────────────────────────────────────────────────

export interface Contrato {
  id: string;
  tipo: TipoContrato;
  jornada: Jornada;
  horasSemana?: number;
  fechaInicio: string;
  fechaFin?: string;
  periodoPruebaHasta?: string;
  categoria?: string;
  documentoId?: string;
  nota?: string;
  /** El importe va aparte, en `api.getDatosSensibles()`. */
  tieneSalario: boolean;
}

export interface DocumentoEmpleado {
  id: string;
  tipo: TipoDocumento;
  fileName: string;
  contentType: string;
  size: number;
  caduca?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface Ausencia {
  id: string;
  tipo: TipoAusencia;
  desde: string;
  hasta: string;
  diasNaturales: number;
  descuentaVacaciones: boolean;
  nota?: string;
  documentoId?: string;
  createdAt: string;
  createdBy?: string;
}

export interface Empleado {
  id: string;
  type: 'empleado';
  lavanderiaId: string | null;
  lavanderiaDesincronizada?: boolean;
  candidatoId?: string;

  nombre: string;
  apellidos: string;
  nombreCompleto: string;
  /** Sin el número: eso está en `api.getDatosSensibles()`. */
  documentoIdentidad?: { tipo: TipoDocumentoIdentidad; caduca?: string };
  tieneNumeroSeguridadSocial: boolean;
  tieneIban: boolean;
  fechaNacimiento?: string;
  nacionalidad?: string;

  telefono?: string;
  telefonoNormalizado?: string;
  telefonoSecundario?: string;
  email?: string;
  direccion?: { calle?: string; localidad?: string; provincia?: string; cp?: string };
  contactoEmergencia?: { nombre?: string; telefono?: string; relacion?: string };
  idiomas: { codigo: string; nivel: NivelIdioma }[];
  carnetConducir: Ternario;
  vehiculoPropio: Ternario;

  puesto?: string;
  zona?: string;
  situacion: Situacion;
  fechaAlta?: string;
  fechaBaja?: string;
  motivoBaja?: MotivoBaja;
  contratos: Contrato[];
  diasVacacionesAnuales?: number;

  documentos: DocumentoEmpleado[];
  ausencias: Ausencia[];

  notas?: string;
  etiquetas: string[];
  conservarHasta?: string;
  caducado?: boolean;

  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

/** Fila de la Plantilla: lo justo para pintar la tabla. */
export interface FilaEmpleado {
  id: string;
  nombreCompleto: string;
  puesto?: string;
  zona?: string;
  situacion: Situacion;
  telefono?: string;
  telefonoNormalizado?: string;
  email?: string;
  etiquetas: string[];
  lavanderiaId: string | null;
  lavanderiaDesincronizada: boolean;
  caducado: boolean;
  contratoVigente: { tipo: TipoContrato; jornada: Jornada; fechaFin?: string } | null;
  sinContratoVigente: boolean;
}

export interface ListadoPersonal {
  empleados: FilaEmpleado[];
  total: number;
  deAlta: number;
  deBaja: number;
}

export interface Operativa {
  existe: boolean;
  nombreEnLavanderia?: string;
  tieneNukiCode?: boolean;
  nukiDesde?: string;
  housekeepingActivo?: boolean;
  housekeepingUsuario?: string;
  housekeepingRol?: string;
  activoEnLavanderia?: boolean;
  discrepaSituacion?: boolean;
}

export interface SaldoVacaciones {
  anio: number;
  anuales: number;
  correspondientes: number;
  consumidos: number;
  restantes: number;
  prorrateado: boolean;
  diasTrabajadosEnElAnio: number;
}

export interface FichaEmpleado {
  empleado: Empleado;
  operativa: Operativa | null;
  vacaciones: SaldoVacaciones;
  avisoSinContrato: boolean;
}

export interface DatosSensibles {
  documentoIdentidad: { tipo: TipoDocumentoIdentidad; numero: string; caduca?: string } | null;
  numeroSeguridadSocial: string | null;
  iban: string | null;
  salarios: Record<string, number | null>;
}

export type TipoVencimiento = 'contrato' | 'periodo_prueba' | 'documento';

export interface Vencimiento {
  empleadoId: string;
  empleadoNombre: string;
  tipo: TipoVencimiento;
  fecha: string;
  dias: number;
  detalle: string;
  clave: string;
}

export interface DiaFichado {
  fecha: string;
  horas: number;
  retrasoMinutos: number;
  tramoAbierto: boolean;
  entrada?: string;
  salida?: string;
}

export interface ResumenFichajes {
  mes: string;
  diasFichados: number;
  horas: number;
  retrasos: number;
  retrasoMinutos: number;
  tramosAbiertos: number;
  diasSinFichar: string[];
  dias?: DiaFichado[];
  aviso?: string;
}

export interface FilaAusencias {
  id: string;
  nombreCompleto: string;
  puesto?: string;
  zona?: string;
  situacion: Situacion;
  fechaAlta?: string;
  fechaBaja?: string;
  ausencias: Ausencia[];
  /** Días libres del cuadrante de la app de lavandería. Solo se leen. */
  descansos: string[];
}

export interface EmpleadoSinFicha {
  lavanderiaId: string;
  nombre: string;
  activo: boolean;
}

// ─── Ayudas de presentación ───────────────────────────────────────────────

/** `YYYY-MM-DD` → «8 sep 2026». Sin `new Date()` para no mover el día. */
export function fechaCorta(iso?: string | null): string {
  if (!iso) return '—';
  const [anio, mes, dia] = iso.slice(0, 10).split('-');
  const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const m = MESES[Number(mes) - 1];
  return m ? `${Number(dia)} ${m} ${anio}` : iso;
}

/** Cuánto falta o cuánto hace, en lenguaje de persona. */
export function textoDias(dias: number): string {
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'mañana';
  if (dias === -1) return 'ayer';
  if (dias < 0) return `hace ${Math.abs(dias)} días`;
  return `en ${dias} días`;
}

export function formatearTamano(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** El mes de hoy como `YYYY-MM`, contado en Madrid. */
export function mesActual(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' }).slice(0, 7);
}

/** `YYYY-MM` ± n meses. */
export function desplazarMes(mes: string, delta: number): string {
  const anio = Number(mes.slice(0, 4));
  const m = Number(mes.slice(5, 7)) - 1 + delta;
  const d = new Date(Date.UTC(anio, m, 1));
  return d.toISOString().slice(0, 7);
}

/** «Septiembre de 2026», para la cabecera de las vistas mensuales. */
export function nombreDelMes(mes: string): string {
  const [anio, m] = mes.split('-');
  const NOMBRES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];
  return `${NOMBRES[Number(m) - 1] || mes} de ${anio}`;
}
